import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';
import { useBreakpointUp } from '@/hooks/useMediaQuery';
import { useKeyboardOpen } from '@/hooks/useDeviceState';
import { Button } from './Button';

/** Room the floating button needs under the last row of a page. */
const FAB_CLEARANCE = '4.5rem';
/** Scroll travel before the button shrinks or grows, so jitter does nothing. */
const SCROLL_HYSTERESIS_PX = 8;

/**
 * A page's main "create" action: Add patient, New recall, Add expense.
 *
 * From `sm` up it is the primary button in the PageHeader, as before. On a
 * phone it becomes an extended floating action button above the tab bar, the
 * Material pattern for a screen's one primary action. That gives the list a
 * full row back at the top and keeps the action under the thumb wherever the
 * list is scrolled to. It shrinks to its icon while the page scrolls down and
 * grows back on the way up; the label stays the accessible name throughout.
 *
 * One per page. Put it in PageHeader `actions` where the old button was.
 */
export function PageAction({
  icon,
  children,
  onPress,
  isDisabled,
  isLoading,
  'data-tour': tourAnchor,
}: {
  /** Required: the collapsed button is the icon alone. */
  icon: ReactNode;
  /** The label. */
  children: ReactNode;
  onPress: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  'data-tour'?: string;
}) {
  const isPhone = !useBreakpointUp('sm');
  const keyboardOpen = useKeyboardOpen();
  const collapsed = useScrolledDown(isPhone);

  // The last card of a list must not end up under the button.
  useEffect(() => {
    if (!isPhone) return;
    const root = document.documentElement;
    root.style.setProperty('--page-fab-pad', FAB_CLEARANCE);
    return () => {
      root.style.removeProperty('--page-fab-pad');
    };
  }, [isPhone]);

  if (!isPhone) {
    return (
      <Button
        startIcon={icon}
        onPress={onPress}
        isDisabled={isDisabled}
        isLoading={isLoading}
        data-tour={tourAnchor}
      >
        {children}
      </Button>
    );
  }

  // Portalled: the page content animates with a transform on navigation, and
  // a fixed element inside a transformed parent travels with it.
  return createPortal(
    <div
      className={cn(
        'fixed end-4 z-(--z-bottom-nav) print:hidden',
        'transition-[opacity,transform] duration-200 ease-out',
        keyboardOpen && 'pointer-events-none translate-y-4 opacity-0',
      )}
      style={{
        bottom:
          'calc(var(--shell-bottom-nav-pad, 0px) + env(safe-area-inset-bottom, 0px) + 1rem)',
      }}
      inert={keyboardOpen}
    >
      <Button
        onPress={onPress}
        isDisabled={isDisabled}
        isLoading={isLoading}
        data-tour={tourAnchor}
        className={cn(
          'h-14 min-w-14 rounded-full px-4 text-body font-semibold shadow-lg',
          'transition-[gap] duration-200',
          collapsed ? 'gap-0' : 'gap-2',
        )}
        startIcon={
          <span
            aria-hidden
            className="flex size-6 items-center justify-center [&_svg]:size-6"
          >
            {icon}
          </span>
        }
      >
        <span
          className={cn(
            'overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200',
            collapsed ? 'max-w-0 opacity-0' : 'max-w-48 opacity-100',
          )}
        >
          {children}
        </span>
      </Button>
    </div>,
    document.body,
  );
}

/** True after the shell's scroll container has moved down, false after up. */
function useScrolledDown(enabled: boolean): boolean {
  const [down, setDown] = useState(false);

  useEffect(() => {
    const main = document.getElementById('main-content');
    if (!enabled || !main) return;
    let last = main.scrollTop;
    const onScroll = () => {
      const y = main.scrollTop;
      if (Math.abs(y - last) < SCROLL_HYSTERESIS_PX) return;
      setDown(y > last && y > 0);
      last = y;
    };
    main.addEventListener('scroll', onScroll, { passive: true });
    return () => main.removeEventListener('scroll', onScroll);
  }, [enabled]);

  return down;
}
